import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useRouteChange } from '@/hooks/useRouteChange';
import { ROUTE_FROM } from '@/utils/const';
import { PageVisitFrom } from '@/utils/eventlog/eventNewConfig';
import { t } from '@/utils/helper/intl';
import {
  aiRemovalURL,
  aiVideoEditURL,
  avatarVideoProURL,
  faceSwapperURL,
  homeURL,
  imageEnhanceURL,
  imageGenerateURL,
  imageTranslationURL,
  libraryURL,
  productAvatarURL,
  videoGenerateURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/router';

import {
  AIImageGenerate,
  AIRemovalIcon,
  AIVideoEdit,
  AIVideoGenerate,
  CreativeLibrary,
  EnhanceIcon,
  FaceSwap,
  HomeIcon,
  ImageTransitionIcon,
  ProductAvatar,
  ProductAvatar2,
  VideoTransitionIcon,
  VoiceCloneIcon,
} from '../../icons';

type MenuInfo = {
  key: string;
};
export interface MenuItem {
  label: string;
  key?: string;
  icon?: React.ReactNode;
  type?: 'group';
  children?: MenuItem[];
}

export const createMenuList = (): MenuItem[] => {
  return [
    {
      label: t('db1c89e0f6e62f9642018cbb531bbd4f'),
      key: homeURL,
      icon: <HomeIcon />,
    },
    {
      label: t('12e8dc341fc87baa32004dd352a781a0'),
      type: 'group',
      children: [
        {
          label: t('9a1641a16a4cc4df9ffa2a17b6348ae0'),
          key: videoGenerateURL,
          icon: <AIVideoGenerate />,
        },
        {
          label: t('c7741980e9f14c014b9e2182050da31e-2'),
          key: imageGenerateURL,
          icon: <AIImageGenerate />,
        },
        {
          label: t('9613af8a82f375ffce54844fc0a470cc'),
          key: aiVideoEditURL,
          icon: <AIVideoEdit />,
        },
      ],
    },
    {
      label: t('AI'),
      type: 'group',
      children: [
        {
          label: t('merror.translation.title'),
          key: videoTranslateURL,
          icon: <VideoTransitionIcon />,
        },
        {
          label: t('bc.ImageTranslation'),
          key: imageTranslationURL,
          icon: <ImageTransitionIcon />,
        },
      ],
    },
    {
      label: t('bc.avatarvideo'),
      type: 'group',
      children: [
        {
          label: t('bc.AvatarVideoPro'),
          key: avatarVideoProURL,
          icon: <ProductAvatar2 />,
        },
        {
          label: t('Product-avatar-video'),
          key: productAvatarURL,
          icon: <ProductAvatar />,
        },
      ],
    },
    {
      label: t('bc.crativetools'),
      type: 'group',
      children: [
        {
          label: t('2-0'),
          key: faceSwapperURL,
          icon: <FaceSwap />,
        },
        {
          label: t('bc.removal'),
          key: aiRemovalURL,
          icon: <AIRemovalIcon />,
        },
        {
          label: t('ce24a821ce9a84f071a4b291570d64b6'),
          key: voiceCloneURL,
          icon: <VoiceCloneIcon />,
        },
        {
          label: t('bc.ImageEnhance'),
          key: imageEnhanceURL,
          icon: <EnhanceIcon />,
        },
      ],
    },
    {
      label: t('Label-MyResource'),
      type: 'group',
      children: [
        {
          label: t('5e0fcc8fa4c3b0f1c95d2a294a61cc1e'),
          key: libraryURL,
          icon: <CreativeLibrary />,
        },
      ],
    },
  ];
};

export const useRouterMenu = () => {
  const { navigate } = useRouteChange();
  const router = useRouter();

  const menuList = useMemo(() => createMenuList(), []);

  const defaultSelectedKeys = useMemo(
    () => [router.pathname || homeURL],
    [router.asPath],
  );

  const jump = useCallback(
    (menuInfo: MenuInfo) => {
      const { key } = menuInfo;
      sessionStorage.setItem(ROUTE_FROM, PageVisitFrom.SIDEBAR);
      navigate(() => {
        router.replace(key);
      });
    },
    [navigate, router],
  );

  return {
    defaultSelectedKeys,
    menuList,
    jump,
  };
};
